import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';
import type { ChangeEvent, FC, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SecurityToolbarFilterPill } from './securityDashboardToolbar';

export type SecuritySearchableFilterOption = {
  value: string;
  label: string;
};

export type SecuritySearchableFilterProps = {
  value: string | null;
  onChange: (value: string | null) => void;
  /** Options for the current search result set (loaded by parent). */
  options: SecuritySearchableFilterOption[];
  /** Parent fetches options when the menu opens or search changes. */
  onSearch: (query: string) => void;
  loading?: boolean;
  pillLabel: string;
  allLabel: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  selectedFallbackLabel?: string;
  showFilterIcon?: boolean;
  fullWidth?: boolean;
  /** When false, renders nothing. */
  visible?: boolean;
  leadingIcon?: ReactNode;
};

const searchBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 10px',
  margin: '4px 8px 8px',
  border: '1px solid #E2E8F0',
  borderRadius: 6,
  backgroundColor: '#F8FAFC',
};

const searchInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  color: '#1E293B',
  padding: 0,
};

/**
 * Generic searchable Security toolbar filter pill.
 * Data loading stays in the parent (or a hook) via `onSearch` + `options`.
 */
export const SecuritySearchableFilter: FC<SecuritySearchableFilterProps> = ({
  value,
  onChange,
  options,
  onSearch,
  loading = false,
  pillLabel,
  allLabel,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  selectedFallbackLabel = 'Selected',
  showFilterIcon = false,
  fullWidth = false,
  visible = true,
  leadingIcon,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const isOpen = Boolean(anchorEl);
  const applied = Boolean(value);

  const debouncedSearch = useMemo(
    () => debounce((query: string) => onSearch(query), 350),
    [onSearch]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (isOpen) {
      onSearch(search);
    }
  }, [isOpen, onSearch, search]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setSearch(next);
    debouncedSearch(next);
  };

  const handleSelect = (nextValue: string | null) => {
    onChange(nextValue);
    setAnchorEl(null);
    setSearch('');
  };

  const selectedLabel = useMemo(() => {
    if (!value) return pillLabel;
    return options.find((option) => option.value === value)?.label ?? selectedFallbackLabel;
  }, [options, value, pillLabel, selectedFallbackLabel]);

  if (!visible) {
    return null;
  }

  const resolvedLeadingIcon =
    leadingIcon ??
    (showFilterIcon ? <FilterListIcon style={{ fontSize: 18 }} /> : null);

  return (
    <>
      <SecurityToolbarFilterPill
        applied={applied}
        label={selectedLabel}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        leadingIcon={resolvedLeadingIcon}
        boxStyleOverrides={{
          cursor: 'pointer',
          width: fullWidth ? '100%' : 'auto',
          flex: fullWidth ? undefined : '0 0 auto',
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          setAnchorEl(null);
          setSearch('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { mt: 0.5, minWidth: 240, maxHeight: 360 },
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key !== 'Escape' && event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
              event.stopPropagation();
            }
          },
        }}
        TransitionProps={{
          onEntered: () => searchInputRef.current?.focus(),
        }}
      >
        <Box style={searchBoxStyle}>
          <SearchIcon style={{ fontSize: 16, color: '#94A3B8' }} />
          <input
            ref={searchInputRef}
            value={search}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            style={searchInputStyle}
          />
          {loading ? <CircularProgress size={14} /> : null}
        </Box>

        <MenuItem
          selected={!value}
          onClick={() => handleSelect(null)}
          style={{ fontSize: 13 }}
        >
          {allLabel}
        </MenuItem>

        {options.length === 0 && !loading ? (
          <Box style={{ padding: '8px 16px', fontSize: 12, color: '#94A3B8' }}>
            {emptyMessage}
          </Box>
        ) : null}

        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => handleSelect(option.value)}
            style={{ fontSize: 13 }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
